import React from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, ColorTokens, theme } from '@shapeable/theme';

import { EmbedComponentsProvider, LayoutShell, LinkTargetFieldsProvider, SiteFooterLayout, UiConfig, UiProvider, useEmbedComponents, ContentEntityCard, EntityTypeCardsProvider, LinkEntityCard, EntityDataHooksProvider, useModal, ComponentSwapProvider } from '@shapeable/ui';
import { VERY_LIGHT_BROWN, DARK_BLUE, BLUE, MEDIUM_BROWN } from '../theme';
import * as pageLayouts from './page-layouts';
import * as sliceLayouts from './slice-layouts';
// import * as sliceLayouts from './slice-layouts';
import { LANG_STRINGS } from '../lang';
import { PostsProvider } from '../gatsby/providers/posts-provider';
import { MenusProvider } from '../gatsby/providers/menus-provider';
import { LayoutStyles, themeOverrides } from '../theme';
import { EmergingTechnologiesIcon } from './icons/emerging-technologies-icon';
import { EnergyTransitionIcon } from './icons/energy-transition-icon';
import { NaturePositiveIcon } from './icons/nature-positive-icon';
import { NetZeroCarbonEmissionsIcon } from './icons/net-zero-carbon-emissions-icon';
import { PlanetaryBoundariesIcon } from './icons/planetary-boundaries-icon';
import { SystemsLeadershipIcon } from './icons/systems-leadership-icon';
import { ChallengesProvider } from '../gatsby/providers/challenges-provider';
import { VillarsLogomarkIcon } from './icons/villars-logomark-icon';
import { useReflections } from '../hooks/use-reflections';
import { ReflectionsProvider } from '../gatsby/providers';
import { PodcastsProvider } from '../gatsby/providers/podcasts-provider';
import { VideosProvider } from '../gatsby/providers/videos-provider';
import { VideoCard } from './entities/video-card';
import { PostCard } from './entities/post-card';
import { EntityFeatureGridIframeButton } from './elements/entity-feature-grid-iframe-button';
import { SubscribeIframe } from './elements/subscribe-iframe';
import { ContactUsIframeEmbed } from './elements/contact-us-iframe-embed';
// import { EntityFeatureGridIframeButton } from './elements/entity-feature-grid-iframe-button';

export type LayoutPropTypes = Classable & HasChildren & {
  path?: string;
  data?: any;
};

const LayoutDefaultProps: LayoutPropTypes = {
};

// -------- Styles --------> 

const ContainerStyles = breakpoints({
  base: css`
    ${LayoutStyles};
    min-height: 100vh;
  `,
});


const FooterStyles = breakpoints({
  base: css`
    flex-grow: 0;
    flex-shrink: 0;
  `,
});

const BodyStyles = breakpoints({
  base: css`
    flex-grow: 1;
  `,
});

// -------- Components -------->

const My = {
  Container: styled(LayoutShell)`${ContainerStyles}`,

  Body: styled.div`${BodyStyles}`,

  // Advertisement: styled(AdvertisementEmbed)`${AdStyles}`,
  
  Footer: styled(SiteFooterLayout)`${FooterStyles}`,
};

export const Layout: React.FC<LayoutPropTypes> = (props) => {
  const { className, children } = props;

  const modal = useModal();

  const embedComponents = useEmbedComponents();

  const ref = React.useRef<HTMLDivElement>(null);

  // Handle opening subscribe iframe modal
  const onHeaderButtonClick = (slug: string) => {
    console.log('Header button clicked with slug:', slug);
    if (slug === 'subscribe') {
      modal?.show(<SubscribeIframe />, { color: '#000000', title: 'Subscribe', opacity: 0.5 });
    } else if (slug === 'contact-us-iframe-button') {
      modal?.show(<ContactUsIframeEmbed />, { color: '#000000', title: 'Contact Us', opacity: 0.5 });
    }
  }

  const config: UiConfig = {
    layouts: { pageLayouts, sliceLayouts, defaultPageLayout: pageLayouts.PageLayoutDefault },
    langStrings: LANG_STRINGS,
    theme: themeOverrides,
    icons: {
      EmergingTechnologiesIcon,
      EnergyTransitionIcon,
      NaturePositiveIcon,
      NetZeroCarbonEmissionsIcon,
      PlanetaryBoundariesIcon,
      SystemsLeadershipIcon,
      VillarsLogomarkIcon,
    },
    header: {
      barHeight: 3,
      showThemeMode: true,
      onHeaderButtonClick,
    },
    search: {
      isAvailable: true,
      extraDataHooks: {
        Reflection: useReflections,
      }
    },
    entity: {
      dataProviders: [
        PostsProvider, MenusProvider, ChallengesProvider, ReflectionsProvider, PodcastsProvider,
        VideosProvider,
      ],
    },
    entityType: {
      Reflection: { gridSort: { fieldName: 'published', desc: true } },
    },
    componentProps: {
      PageMeta: {
        showBadge: true,
        alignAuthors: true,
        useTooltip: true,
      },
      ContentEntityMeta: {
        entityStackProps: {
          badgePath: 'role.name',
          badgeFilter: 'Fellow'
        }
      },
      SiteMenuBar: {
        colors: {
          background: DARK_BLUE,
        }
      },
      PageHeader: {
        themeMode: 'dark',
      },
      SiteFooterLayout: {
        colors: {
          background: VERY_LIGHT_BROWN,
          dark: {
            background: 'dark',
          }
        }
      },
      MenuItem: {
        colors: {
          bold: BLUE,
          hover: {
            text: 'strong',
            bold: 'strong',
            background: BLUE,
          },
          active: {
            text: MEDIUM_BROWN,
          },
        }
      },
    }
  }

  return (
    <UiProvider value={config}>
    <LinkTargetFieldsProvider value={["reflection"]}>
    <ComponentSwapProvider value={{
      EntityFeatureGridCustomAction: EntityFeatureGridIframeButton,
    }}>
    <EntityTypeCardsProvider value={{
      Reflection: ContentEntityCard,
      Link: LinkEntityCard,
      Video: VideoCard,
      Post: PostCard,
    }}>
    <EmbedComponentsProvider value={{
      ...embedComponents,
      }}>
    <EntityDataHooksProvider value={{
      Reflection: useReflections,
    }}>
      <My.Container
        id="#container"
        ref={ref}
        hideOverflow={false}
        className={className}
        includeTooltip
        modalColor={DARK_BLUE}
        tooltipProps={{
          backgroundColor: VERY_LIGHT_BROWN,
          globalEventOff: 'click',
        }}
      >
        <My.Body>
          {children}
        </My.Body>
        <My.Footer />
      </My.Container>
    </EntityDataHooksProvider>
    </EmbedComponentsProvider>
    </EntityTypeCardsProvider>
    </ComponentSwapProvider>
    </LinkTargetFieldsProvider>
    </UiProvider>
  );
  
  
};

/* @ts-ignore */
Layout.defaultProps = LayoutDefaultProps;

